import React from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { 
  Users, 
  UserCheck, 
  UserX, 
  Calendar,
  ArrowRight
} from 'lucide-react'
import { dashboardService } from '@/services/dashboardService'

const HROverview = () => {
  const { data: hrData, isLoading } = useQuery(
    'dashboard-hr-overview',
    dashboardService.getHROverview,
    {
      select: (response) => response.data,
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    }
  )

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">HR Overview</h3>
          <Link
            to="/hr"
            className="text-sm text-primary-600 hover:text-primary-500 flex items-center"
          >
            View all
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center">
              <Users className="h-5 w-5 text-blue-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Total Employees</p>
                <p className="text-xs text-gray-500">Active workforce</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-blue-600">
                {hrData?.data?.totalEmployees || 0}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center">
              <UserCheck className="h-5 w-5 text-green-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Present Today</p>
                <p className="text-xs text-gray-500">Attendance rate</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-green-600">
                {hrData?.data?.presentToday || 0}
              </p>
              <p className="text-xs text-gray-500">
                {hrData?.data?.attendanceRate || 0}%
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-yellow-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">On Leave</p>
                <p className="text-xs text-gray-500">Today</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-yellow-600">
                {hrData?.data?.onLeave || 0}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
            <div className="flex items-center">
              <UserX className="h-5 w-5 text-red-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Absent</p>
                <p className="text-xs text-gray-500">Without leave</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-red-600">
                {hrData?.data?.absent || 0}
              </p>
            </div>
          </div>
        </div>

        {hrData?.data?.pendingLeaveRequests && hrData?.data?.pendingLeaveRequests?.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Pending Leave Requests</h4>
            <div className="space-y-2">
              {hrData?.data?.pendingLeaveRequests?.slice(0, 3).map((request, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 truncate">{request.employeeName}</span>
                  <span className="text-xs text-gray-500">
                    {request.leaveType} - {request.days} days
                  </span>
                </div>
              ))}
            </div>
            {hrData?.data?.pendingLeaveRequests?.length > 3 && (
              <p className="text-xs text-gray-500 mt-2">
                +{hrData?.data?.pendingLeaveRequests?.length - 3} more requests
              </p>
            )}
          </div>
        )}

        {hrData?.data?.upcomingBirthdays && hrData?.data?.upcomingBirthdays?.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Upcoming Birthdays</h4>
            <div className="space-y-2">
              {hrData?.data?.upcomingBirthdays?.slice(0, 3).map((birthday, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 truncate">{birthday.name}</span>
                  <span className="text-xs text-gray-500">{birthday.date}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HROverview